import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  CardContent,
  CardDescription,
  CardFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InfiniteScroll,
  Input,
  Label,
  LoadingSpinner,
  ScrollArea,
  TrailingDateTimePickerWithRange,
  TruncatedText,
  useToast,
} from '@ais-ui/ui-core';
import Map from 'ol/Map';
import React, { useEffect, useState } from 'react';
import {
  AutoCompleteShipInfo,
  ExtractedTargetInfo,
  extractTargetInfos,
  getTargetInfoByMMSI,
  InvalidMMSIError,
  useSelectMessageTargets,
  useSession,
} from '@ais-ui/websockets';
import { cn, isNonEmpty, useTimeZoneContext } from '@ais-ui/utils';
import { v4 as uuidv4 } from 'uuid';
import {
  PiBroadcastThin,
  PiChatsCircleThin,
  PiDotsThreeVertical,
} from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { NoItemsEmptyStateList } from '../../components/EmptyList';
import { ReceivedMessageWithDate, useMessagesReceivedList } from '@ais-ui/api';
import { filterReceivedMessages } from './utils';
import { ReceivedMessage } from '@ais-ui/api-spec/config';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { DateRange } from 'react-day-picker';

import { useAISMapContext } from '../Map/AISMapContext';
import { MessageActionsMenu } from './MessageActionsMenu';
import {
  drawSenderToReceiver,
  Position,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { Extent } from 'ol/extent';
import { fromLonLat } from 'ol/proj';
import { Session } from '@ais-ui/auth';
import { useSelectionState } from '../../components/SelectionStateProvider';

interface ReceivedMessageGridItemProps {
  label: string;
  value: string | undefined | null;
}
const ReceivedMessageGridItem = ({
  label,
  value,
}: ReceivedMessageGridItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

const onShowAddressed = async (
  receivedMessage: ReceivedMessageWithDate,
  selectMessageTarget: (
    senderMmsi: number,
    receiverMmsi: number,
  ) => Promise<any>,
  setTargetInfoDetailsList: React.Dispatch<
    React.SetStateAction<ExtractedTargetInfo[]>
  >,
  map: Map,
  toast: ReturnType<typeof useToast>['toast'],
  olSession: Session,
) => {
  const receiverMmsi = receivedMessage?.destination?.mmsi
    ? receivedMessage.destination.mmsi
    : -1;
  const senderMmsi = receivedMessage?.source?.mmsi
    ? receivedMessage.source.mmsi
    : -1;
  selectMessageTarget(senderMmsi, receiverMmsi);

  try {
    const receiverQueryPoint = await getTargetInfoByMMSI(
      olSession,
      receiverMmsi,
    );
    const receiverTarget = extractTargetInfos(receiverQueryPoint);

    const senderQueryPoint = await getTargetInfoByMMSI(olSession, senderMmsi);
    const senderTarget = extractTargetInfos(senderQueryPoint);

    setTargetInfoDetailsList((prevTargets: ExtractedTargetInfo[]) => {
      const newTargets = [];
      if (receiverTarget.extractedTargetInfo.length > 0) {
        newTargets.push(receiverTarget);
      }
      if (senderTarget.extractedTargetInfo.length > 0) {
        newTargets.push(senderTarget);
      }
      return [...newTargets, ...prevTargets];
    });
  } catch (error) {
    if (error instanceof InvalidMMSIError) {
      toast({
        title: 'Invalid MMSI',
        description: 'Cannot display message targets with invalid MMSI numbers',
        variant: 'warning',
      });
      return;
    }
    throw error;
  }

  const receiverPosition = receivedMessage?.destination?.position;
  const senderPosition = receivedMessage?.source?.position;

  if (receiverPosition && senderPosition) {
    const transformedReceiverPosition = fromLonLat([
      receiverPosition?.coordinates?.[0] ?? 0,
      receiverPosition?.coordinates?.[1] ?? 0,
    ]);

    const transformedSenderPosition = fromLonLat([
      senderPosition?.coordinates?.[0] ?? 0,
      senderPosition?.coordinates?.[1] ?? 0,
    ]);

    const extent: Extent = [
      Math.min(transformedReceiverPosition[0], transformedSenderPosition[0]),
      Math.min(transformedReceiverPosition[1], transformedSenderPosition[1]),
      Math.max(transformedReceiverPosition[0], transformedSenderPosition[0]),
      Math.max(transformedReceiverPosition[1], transformedSenderPosition[1]),
    ];

    map.getView().fit(extent, { duration: 0, padding: [50, 50, 50, 50] });

    drawSenderToReceiver(
      map,
      receiverPosition.coordinates as Position,
      senderPosition.coordinates as Position,
    );
  } else {
    toast({
      className: 'message-no-position',
      title: 'Missing position!',
      description: `Missing position on sender or receiver.`,
      variant: 'destructive',
    });
  }
};

const onShowBroadcast = (
  receivedMessage: ReceivedMessageWithDate,
  setZoomToShip: (newZoomToShip: AutoCompleteShipInfo | null) => void,
) => {
  const mmsi = receivedMessage?.source?.mmsi
    ? receivedMessage.source.mmsi.toString()
    : null;
  const callSign = receivedMessage?.source?.callsign || '';
  const imo = receivedMessage?.source?.imo
    ? receivedMessage.source.imo.toString()
    : null;
  const name = receivedMessage?.source?.name || '';

  let type = 'TT_SHIP';

  if (mmsi && mmsi.startsWith('00')) {
    type = 'Basestation';
  }

  const newSelectedShip: AutoCompleteShipInfo = {
    callSign,
    imo,
    mmsi,
    name,
    type,
  };
  setZoomToShip(newSelectedShip);
};

interface ReceivedMessageDropDownMenuProps {
  receivedMessage: ReceivedMessageWithDate;
  className?: string;
}

const ReceivedMessageDropDownMenu = ({
  receivedMessage,
  className,
}: ReceivedMessageDropDownMenuProps) => {
  const { messages } = useLocalization();
  const { olSession } = useSession();
  const selectMessageTarget = useSelectMessageTargets();
  const { setTargetInfoDetailsList } = useSelectionState();
  const { setZoomToShip } = useAISMapContext();
  const { map } = useOpenLayersContext();
  const { toast } = useToast();

  const onShow = () => {
    if (receivedMessage.addressed) {
      onShowAddressed(
        receivedMessage,
        selectMessageTarget,
        setTargetInfoDetailsList,
        map,
        toast,
        olSession,
      );
    } else {
      onShowBroadcast(receivedMessage, setZoomToShip);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button size="icon" variant="ghost" className="size-7">
          <PiDotsThreeVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>Show</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface SenderInfoProps {
  receivedMessage: ReceivedMessageWithDate;
}

const SenderInfo = ({ receivedMessage }: SenderInfoProps) => {
  return (
    <>
      <Label className="text-lg">Sender</Label>
      <div className="grid grid-cols-2 gap-4">
        <ReceivedMessageGridItem
          label="Name"
          value={receivedMessage?.source?.name}
        />
        <ReceivedMessageGridItem
          label="MMSI"
          value={
            receivedMessage?.source?.mmsi
              ? receivedMessage.source.mmsi.toString()
              : ''
          }
        />
        <ReceivedMessageGridItem
          label="IMO"
          value={
            receivedMessage?.source?.imo
              ? receivedMessage.source.imo.toString()
              : ''
          }
        />
        <ReceivedMessageGridItem
          label="Callsign"
          value={receivedMessage?.source?.callsign}
        />
      </div>
    </>
  );
};

interface ReceiverInfoProps {
  receivedMessage: ReceivedMessageWithDate;
}

const ReceiverInfo = ({ receivedMessage }: ReceiverInfoProps) => {
  return (
    <>
      <Label className="text-lg">Receiver</Label>
      <div className="grid grid-cols-2 gap-4">
        <ReceivedMessageGridItem
          label="Name"
          value={receivedMessage?.destination?.name}
        />
        <ReceivedMessageGridItem
          label="MMSI"
          value={
            receivedMessage?.destination?.mmsi
              ? receivedMessage.destination.mmsi.toString()
              : ''
          }
        />
        <ReceivedMessageGridItem
          label="IMO"
          value={
            receivedMessage?.destination?.imo
              ? receivedMessage.destination.imo.toString()
              : ''
          }
        />
        <ReceivedMessageGridItem
          label="Callsign"
          value={receivedMessage?.destination?.callsign}
        />
      </div>
    </>
  );
};

interface ReceivedMessageItemProps {
  receivedMessage: ReceivedMessageWithDate;
}

const ReceivedMessageItem = ({ receivedMessage }: ReceivedMessageItemProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const receivedMessageSenderText = receivedMessage?.source?.name
    ? receivedMessage?.source?.name
    : receivedMessage?.source?.mmsi;

  const receivedMessageReceiverText = receivedMessage?.destination?.name
    ? receivedMessage?.destination?.name
    : receivedMessage?.destination?.mmsi;

  const addressedMessage = `${receivedMessageSenderText} to ${receivedMessageReceiverText}`;

  const broadcastMessage = `Broadcast by ${receivedMessageSenderText}`;

  return (
    <AccordionItem
      key={receivedMessage.id}
      value={receivedMessage.id ? receivedMessage.id : uuidv4()}
      className={cn('px-4')}
    >
      <div className="flex justify-between items-center">
        <div className="flex-grow">
          <AccordionTrigger className="py-2" hideChevron={true}>
            <span className="overflow-hidden whitespace-nowrap w-[265px]">
              <div className="flex items-center">
                <div className="relative overflow-visible flex-shrink-0">
                  {receivedMessage.addressed ? (
                    <PiChatsCircleThin className="h-6 w-6" />
                  ) : (
                    <PiBroadcastThin className="h-6 w-6" />
                  )}
                </div>
                <div className="ml-4 space-y-1 min-w-0 flex-1">
                  <TruncatedText
                    text={
                      receivedMessage.addressed
                        ? addressedMessage
                        : broadcastMessage
                    }
                    className="text-sm text-left font-medium leading-none group-hover:underline"
                    maxWidth="224px"
                  />
                  <p className="truncate text-sm text-left text-muted-foreground">
                    {formatDateTimeWithTimeZone(receivedMessage.time)}
                  </p>
                </div>
              </div>
            </span>
          </AccordionTrigger>
        </div>
        <ReceivedMessageDropDownMenu
          receivedMessage={receivedMessage}
        ></ReceivedMessageDropDownMenu>
      </div>

      <AccordionContent>
        <div className="grid gap-6 px-4 pt-4">
          <div className="grid gap-4">
            <SenderInfo receivedMessage={receivedMessage}></SenderInfo>
            {receivedMessage.addressed && (
              <ReceiverInfo receivedMessage={receivedMessage}></ReceiverInfo>
            )}
            <ReceivedMessageGridItem
              label="Text"
              value={receivedMessage.text}
            />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export const ReceivedMessages = () => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');
  const [isTrailing, setIsTrailing] = React.useState(true);
  const [filteredReceivedMessages, setFilteredReceivedMessages] = useState<
    ReceivedMessageWithDate[]
  >([]);

  const [date, setDate] = React.useState<DateRange | undefined>({
    from: new Date(new Date().getTime() - 1 * 60 * 60 * 1000),
    to: new Date(),
  });

  const { data, isLoading } = useMessagesReceivedList(date?.from, date?.to);
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();

  useEffect(() => {
    // If trailing and loading, retain the existing state
    if (isTrailing) {
      if (isLoading) {
        return;
      }
      if (!data) {
        return;
      }
      if (data?.records?.length === 0) {
        return;
      }
    }

    if (!isTrailing) {
      if (isLoading) {
        setFilteredReceivedMessages([]);
        return;
      }
    }

    const receivedMessages = data?.records || [];

    if (search === '') {
      setFilteredReceivedMessages(receivedMessages);
    } else {
      setFilteredReceivedMessages(
        filterReceivedMessages(receivedMessages, search),
      );
    }
  }, [data, search, isTrailing, isLoading]);

  return (
    <>
      <CardContent className="space-y-4">
        <CardDescription>{messages.messagesSubtitle}</CardDescription>
        <TrailingDateTimePickerWithRange
          date={date}
          setDate={setDate}
          timeZone={timeZone}
          dateTimeFormat={userDateFormat}
          entity="message"
          isTrailing={isTrailing}
          setIsTrailing={setIsTrailing}
        ></TrailingDateTimePickerWithRange>
        <Input
          id="search"
          placeholder={messages.messagesSearchMessages}
          className="h-10"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
      </CardContent>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        {!isLoading && data?.records?.length === 0 && (
          <NoItemsEmptyStateList
            entity="received message"
            srcImage="empty_list_bro.png"
          ></NoItemsEmptyStateList>
        )}
        {filteredReceivedMessages.length === 0 && isLoading && (
          <LoadingSpinner></LoadingSpinner>
        )}
        {filteredReceivedMessages.length > 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <InfiniteScroll
              items={filteredReceivedMessages}
              limit={30}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              renderItem={(receivedMessage) => {
                return (
                  <ReceivedMessageItem
                    key={receivedMessage.id}
                    receivedMessage={receivedMessage}
                  />
                );
              }}
            />
          </Accordion>
        )}
      </CardContent>
      <CardFooter className="flex flex-grow-0 flex-col items-end">
        <MessageActionsMenu buttonText="Actions" />
      </CardFooter>
    </>
  );
};
